import { Button, IconButton, InputAdornment, TextField, CircularProgress, Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetMyChatsQuery, useGetRoomMessagesQuery, useDeleteChatMutation } from "../../service/chat";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSocketContext } from "../../helpers/sockets/socketProvider";
import useAuth from "../../hooks/useAuth";
import { Conversation } from "../../types/General";
import { useSocketEvent } from "../../helpers/sockets/socketEvent";
import dayjs from "dayjs";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from "@mui/icons-material/Check";
import SearchBar from "../../components/SearchBar";
import { UploadMedia, Uploadpdf } from "../../utils/mediaUpload";
import { showError, showToast } from "../../constants/toasts";

const Chat = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const roomIdFromUrl = searchParams.get("roomId");
    const user = useAuth();
    const { socket, isConnected } = useSocketContext();
    const [activeChat, setActiveChat] = useState<string>(roomIdFromUrl || "");
    const activeChatRef = useRef<string>(activeChat);
    const previousRoomRef = useRef<string>("");

    useEffect(() => {
        activeChatRef.current = activeChat;
    }, [activeChat]);

    const [activeChatDetails, setActiveChatDetails] = useState<any>(null);
    const [rtActive, setRtActive] = useState(false);
    const [messages, setMessages] = useState<any[]>([]);
    const [messageInput, setMessageInput] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState<Record<string, boolean>>({});

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const prevScrollHeightRef = useRef<number>(0);
    const isPaginationRef = useRef<boolean>(false);
    const isNewMessageRef = useRef<boolean>(false);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [filePreview, setFilePreview] = useState<string | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleFileSelection = (file: File) => {
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        if (file.size > MAX_FILE_SIZE) {
            showError("File size exceeds the allowed limit (5MB)");
            return;
        }
        
        if (!file.type.startsWith("image/") && !file.type.includes("pdf") && !file.name.match(/\.(doc|docx)$/i)) {
            showError("Unsupported file format");
            return;
        }

        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setFilePreview(url);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragActive) setIsDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelection(e.dataTransfer.files[0]);
        }
    };
    
    const removeSelectedFile = () => {
        setSelectedFile(null);
        if (filePreview) {
            URL.revokeObjectURL(filePreview);
            setFilePreview(null);
        }
    };

    useEffect(() => {
        return () => {
            if (filePreview) URL.revokeObjectURL(filePreview);
        };
    }, [filePreview]);

    const { data, isLoading: chatsLoading } = useGetMyChatsQuery({ search: debouncedSearchTerm });
    const [deleteChat, { isLoading: isDeletingChat }] = useDeleteChatMutation();

    const handleClearChat = async () => {
        if (!activeChat) return;
        const confirmClear = window.confirm("Are you sure you want to clear this chat?");
        if (!confirmClear) return;

        try {
            // TODO: Uncomment and use the new clear API once backend provides it
            // await clearChatApi(activeChat).unwrap(); 
            showToast("Chat cleared from screen (waiting for new backend API).");
            setMessages([]);
        } catch (error) {
            showError("Failed to clear chat. Please try again.");
            console.error("Clear chat error:", error);
        }
    };

    const [chatList, setChatList] = useState<Conversation[]>([]);
    useEffect(() => {
        if (data?.data?.data) {
            setChatList(data.data.data);
        } else {
            setChatList([]);
        }
    }, [data]);

    useEffect(() => {
        if (activeChat && chatList?.length > 0) {
            const chat = chatList.find((c) => c.id === activeChat);
            if (chat) {
                setActiveChatDetails(chat);
            }
        }
    }, [activeChat, chatList]);

    const {
        data: roomMessages,
        isLoading: roomMessagesLoading,
        isFetching: roomMessagesFetching,
        refetch,
    } = useGetRoomMessagesQuery(
        { roomId: activeChat, page, limit: 15 },
        { skip: !activeChat },
    );

    useEffect(() => {
        if (roomMessages?.data?.list) {
            const fetchedList = roomMessages.data.list;
            if (page === 1) {
                setMessages(fetchedList);
            } else {
                setMessages((prev) => {
                    const existingIds = new Set(prev.map((m: any) => m.id));
                    const uniqueNew = fetchedList.filter((m: any) => !existingIds.has(m.id));
                    return [...uniqueNew, ...prev];
                });
            }
            setHasMore(fetchedList.length >= 15);
        } else if (page === 1) {
            setMessages([]);
        }
    }, [roomMessages, page, activeChat]);

    const chatBodyRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        if (chatBodyRef.current) {
            if (isPaginationRef.current) {
                chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight - prevScrollHeightRef.current;
                isPaginationRef.current = false;
            } else if (page === 1 || isNewMessageRef.current) {
                scrollToBottom();
                isNewMessageRef.current = false;
            }
        }
    }, [messages, page]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (e.currentTarget.scrollTop === 0 && hasMore && !roomMessagesFetching && !roomMessagesLoading) {
            isPaginationRef.current = true;
            prevScrollHeightRef.current = e.currentTarget.scrollHeight;
            setPage((prev) => prev + 1);
        }
    };

    const handleChatClick = (id: string) => {
        setActiveChat(id);
        if (window.innerWidth < 911) {
            setRtActive(true);
        }
    };

    const handleChatListing = () => {
        setRtActive(false);
    };

    const handleSendMessage = async () => {
        if ((!messageInput.trim() && !selectedFile) || !activeChat || !socket) return;
        if (!socket.connected) {
            showError("Chat server disconnected. Please refresh or try again.");
            return;
        }

        if (selectedFile) {
            try {
                setIsUploading(true);
                let uploadedUrl = "";
                if (selectedFile.type.startsWith("image/")) {
                    const res = await UploadMedia(selectedFile);
                    if (res?.data?.url || res?.data?.image || res?.data?.imageUrl) {
                        uploadedUrl = res.data.url || res.data.image || res.data.imageUrl;
                    }
                } else {
                    const res = await Uploadpdf(selectedFile);
                    if (res?.data?.url || res?.data?.image || res?.data?.imageUrl) {
                        uploadedUrl = res.data.url || res.data.image || res.data.imageUrl;
                    }
                }
                
                if (uploadedUrl) {
                    if (!socket.connected) {
                        showError("Connection lost during upload. Please try again.");
                        return;
                    }
                    socket.emit("chat:send", {
                        roomId: activeChat,
                        message: uploadedUrl,
                    });
                    isNewMessageRef.current = true;
                    showToast("Media uploaded successfully.");
                } else {
                    showError("Upload failed due to a network/server error");
                    return;
                }
            } catch (error) {
                showError("Upload failed due to a network/server error");
                console.error("Upload error:", error);
                return;
            } finally {
                setIsUploading(false);
                removeSelectedFile();
            }
        }

        if (messageInput.trim()) {
            socket.emit("chat:send", {
                roomId: activeChat,
                message: messageInput,
            });
            setMessageInput("");
            isNewMessageRef.current = true;
        }
    };

    useEffect(() => {
        if (!activeChat || !socket || !isConnected) return;

        // Clear the roomId from the URL if it exists
        if (searchParams.has("roomId")) {
            navigate("/chat", { replace: true });
        }

        setPage(1);
        setHasMore(true);
        isPaginationRef.current = false;
        refetch();

        // Leave previous room if we switched chats
        if (previousRoomRef.current && previousRoomRef.current !== activeChat) {
            socket.emit("chat:leave", { roomId: previousRoomRef.current });
        }
        previousRoomRef.current = activeChat;

        // Join new room
        socket.emit("chat:join", { roomId: activeChat, chatId: activeChat });
        socket.emit("chat:readRoom", { roomId: activeChat });
        
        setChatList((prev) =>
            prev.map((chat) =>
                chat.id === activeChat ? { ...chat, unreadCount: 0 } : chat
            )
        );
    }, [socket, activeChat, isConnected, refetch, navigate, searchParams]);

    const handleChatEvent = useCallback((data: any) => {
        const payload = data?.data || data;

        if (data?.type === "ROOM_MESSAGES_READ") {
            const roomId = payload?.roomId;
            const readUserId = payload?.userId;
            if (!roomId) return;

            // Only clear unread count if the read event explicitly belongs to the current user
            // If readUserId is missing, it's safer NOT to clear inactive chats to prevent auto-read bugs
            const isReadForMe = readUserId 
                ? String(readUserId) === String(user?.id) 
                : String(roomId) === String(activeChatRef.current); // Only assume it's for me if I am actively viewing this room
            
            if (isReadForMe) {
                setChatList((prev) =>
                    prev.map((c) =>
                        String(c.id) === String(roomId) ? { ...c, unreadCount: 0 } : c
                    )
                );
            }
            if (String(roomId) === String(activeChatRef.current)) {
                setMessages((prev) =>
                    prev.map((msg) => ({ ...msg, isRead: true }))
                );
            }
            return;
        }

        if (data?.type === "MESSAGE_READ") {
            const messageId = payload?.messageId;
            if (messageId) {
                setMessages((prev) =>
                    prev.map((msg) =>
                        msg.id === messageId ? { ...msg, isRead: payload?.isRead ?? true } : msg
                    )
                );
            }
            return;
        }

        if (data?.type === "NEW_MESSAGE" || (payload && typeof payload.message === "string")) {
            const msgPayload = payload;
            const incomingRoomId = String(msgPayload?.roomId || msgPayload?.id);
            const activeRoomId = String(activeChatRef.current);
            // Strict room check before treating as current room
            const isCurrentRoom = Boolean(incomingRoomId && activeChatRef.current && incomingRoomId === activeRoomId);

            if (isCurrentRoom) {
                setMessages((prev) => [...prev, msgPayload]);
                isNewMessageRef.current = true;
                if (String(msgPayload?.senderId) !== String(user?.id)) {
                    socket?.emit("chat:readRoom", { roomId: incomingRoomId });
                }
            } else {
                setChatList((prev) =>
                    prev.map((c) => {
                        if (String(c.id) === incomingRoomId) {
                            const myPart = Array.isArray(c.myParticipant) ? c.myParticipant[0] : c.myParticipant;
                            const currentUnread = Number(c.unreadCount !== undefined ? c.unreadCount : parseInt(myPart?.unreadCount || "0", 10)) || 0;
                            return { ...c, unreadCount: currentUnread + 1, lastMessage: msgPayload.message, lastMessageAt: msgPayload.createdAt };
                        }
                        return c;
                    })
                );
            }
        } else {
            console.warn("Ignored non-message chat event:", data);
        }
    }, [activeChat, user?.id, socket]);

    const handleChatJoined = useCallback((data: any) => {
    }, []);

    const handleChatLeave = useCallback((data: any) => {
    }, []);

    useSocketEvent("chat:userStatus", (data: any) => {
        if (data && data.userId) {
            setOnlineUsers((prev) => ({
                ...prev,
                [data.userId]: data.isOnline,
            }));
        }
    });

    useSocketEvent("chat:event", handleChatEvent);
    useSocketEvent("chat:join-success", handleChatJoined);
    useSocketEvent("chat:leave-success", handleChatLeave);
    useSocketEvent("chat:updateRoom", (data: any) => {
        console.log("update received:", data);
        const updatedRoom = data?.id ? data : data?.data;
        if (!updatedRoom) return;

        setChatList((prev) => {
            const existsIndex = prev.findIndex(
                (c) => String(c.id) === String(updatedRoom.id),
            );
            const newList = [...prev];
            if (existsIndex > -1) {
                const existing = newList[existsIndex];
                const myPart = Array.isArray(existing.myParticipant) ? existing.myParticipant[0] : existing.myParticipant;
                const existingUnread = Number(existing.unreadCount !== undefined ? existing.unreadCount : parseInt(myPart?.unreadCount || "0", 10)) || 0;
                
                const updatedObj = { ...existing, ...updatedRoom };
                
                // Preserve manually incremented unread count if updatedRoom doesn't explicitly provide a non-zero one
                const newPart = Array.isArray(updatedRoom.myParticipant) ? updatedRoom.myParticipant[0] : updatedRoom.myParticipant;
                const updatedUnread = Number(updatedRoom.unreadCount !== undefined ? updatedRoom.unreadCount : parseInt(newPart?.unreadCount || "0", 10)) || 0;
                
                // STRICT CHECK: For inactive rooms, do not let chat:updateRoom clear a positive unread count to 0
                // as this causes the badge to disappear if the backend sends stale data
                if (String(updatedRoom.id) !== String(activeChatRef.current)) {
                    if (updatedUnread === 0 && existingUnread > 0) {
                        updatedObj.unreadCount = existingUnread;
                    } else {
                        updatedObj.unreadCount = updatedUnread;
                    }
                } else {
                    updatedObj.unreadCount = 0;
                }
                
                newList.splice(existsIndex, 1);
                return [updatedObj, ...newList];
            } else {
                return [updatedRoom, ...newList];
            }

        });
    });
    return (
        <>
            <div className="main_title">
                <h1>Chat</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
                    Chat
                </p>
            </div>
            <div className="chat_sc wrp_spc">
                <div className="inner">
                    <div className="lt">
                        <div style={{ marginBottom: "15px" }}>
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: any) => setSearchTerm(e.target.value)}
                                onCross={() => {
                                    setSearchTerm("");
                                    setDebouncedSearchTerm("");
                                }}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setDebouncedSearchTerm}
                                placeholder="Search"
                            />
                        </div>
                        {chatList?.length > 0 ? (
                            chatList.map((chat) => (
                                <div
                                    key={chat.id}
                                    className={`chat_single ${activeChat === chat.id ? "active" : ""}`}
                                    onClick={() => {
                                        handleChatClick(chat.id);
                                    }}
                                >
                                    <span className="time">
                                        {chat?.lastMessageAt
                                            ? dayjs(chat?.lastMessageAt).format("hh:mm A")
                                            : ""}
                                    </span>
                                    <figure style={{ position: 'relative' }}>
                                        <img
                                            src={
                                                chat?.otherParticipants[0]?.user?.image ||
                                                "/static/images/user_placeholder.png"
                                            }
                                            alt="icon"
                                        />
                                        <span style={{ 
                                            position: 'absolute', 
                                            bottom: '2px', 
                                            right: '2px', 
                                            width: '12px', 
                                            height: '12px', 
                                            backgroundColor: onlineUsers[chat?.otherParticipants?.[0]?.user?.id] ? '#00d26a' : '#adb5bd', 
                                            borderRadius: '50%', 
                                            border: '2px solid #fff' 
                                        }} />
                                    </figure>
                                    <p style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong>
                                                {chat?.otherParticipants[0]?.user?.fullName || "User"}
                                            </strong>
                                            {(() => {
                                                const myPart = Array.isArray(chat?.myParticipant) ? chat?.myParticipant?.[0] : chat?.myParticipant;
                                                const unreadCountVal = Number(chat.unreadCount !== undefined ? chat.unreadCount : parseInt(myPart?.unreadCount || "0", 10)) || 0;
                                                if (unreadCountVal > 0) {
                                                    return (
                                                        <span className="unread-badge" style={{ backgroundColor: '#d32f2f', color: '#fff', fontSize: '10px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '10px', marginLeft: 'auto' }}>
                                                            {unreadCountVal}
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        <span className="unread">{chat.lastMessage || ""}</span>
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: "center", padding: "20px", color: "#888" }}>
                                <img src="/static/images/nouserfound.svg" alt="No user found" style={{ maxWidth: "150px", opacity: 0.8, marginBottom: "15px" }} />
                                <p>No user found</p>
                            </div>
                        )}
                    </div>

                    <div className={`rt ${rtActive ? "active" : ""}`}>
                        {chatList?.length === 0 ? (
                            <div className="chat_box1" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <img src="/static/images/nochatfound.svg" alt="No chat found" style={{ maxWidth: "200px", opacity: 0.8, marginBottom: "15px" }} />
                                <p style={{ color: "#888", fontSize: "16px" }}>No chat found</p>
                            </div>
                        ) : activeChat ? (
                            <div 
                                className={`chat_box ${isDragActive ? "drag_active" : ""}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={isDragActive ? { border: "2px dashed #007bff", backgroundColor: "rgba(0,123,255,0.05)" } : {}}
                            >
                                <div className="chat_head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {activeChat && (
                                            <>
                                                <figure style={{ position: 'relative' }}>
                                                    <img
                                                        src={
                                                            activeChatDetails?.otherParticipants?.[0]?.user?.image ||
                                                            "/static/images/user_placeholder.png"
                                                        }
                                                        alt="icon"
                                                    />
                                                    <span style={{ 
                                                        position: 'absolute', 
                                                        bottom: '2px', 
                                                        right: '2px', 
                                                        width: '12px', 
                                                        height: '12px', 
                                                        backgroundColor: onlineUsers[activeChatDetails?.otherParticipants?.[0]?.user?.id] ? '#00d26a' : '#adb5bd', 
                                                        borderRadius: '50%', 
                                                        border: '2px solid #fff' 
                                                    }} />
                                                </figure>
                                                <p>
                                                    {
                                                        activeChatDetails?.otherParticipants?.[0]?.user?.fullName || "User"
                                                    }{" "}

                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {activeChat && (
                                        <Tooltip title="Clear Chat" placement="bottom">
                                            <IconButton onClick={handleClearChat} style={{ color: "#ff4d4f", marginRight: "10px" }}>
                                                <DeleteSweepIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>

                                <div className="chat_body" ref={chatBodyRef} onScroll={handleScroll}>
                                    {roomMessagesFetching && page > 1 && (
                                        <div style={{ textAlign: "center", padding: "10px", fontSize: "12px", opacity: 0.6 }}>
                                            Loading older messages...
                                        </div>
                                    )}
                                    {messages?.filter((msg: any) => msg && typeof msg?.message === "string").map((msg: any) => {
                                        const isMe = msg.senderId == user?.id || msg.senderId == (user as any)?.user?.id;
                                        return (
                                            <div
                                                key={msg.id}
                                                className={`single_message ${isMe ? "sended" : "recieved"}`}
                                            >
                                                <figure>
                                                    <img
                                                        src={
                                                            msg?.sender?.image || "/static/images/user_placeholder.png"
                                                        }
                                                        alt="icon"
                                                    />
                                                </figure>
                                                <div className="msg_body">
                                                    {(() => {
                                                        const rawMsg = msg?.message;
                                                        if (typeof rawMsg !== "string") {
                                                            return <p style={{color:'red', fontSize: '12px'}}>Invalid format: {JSON.stringify(rawMsg)}</p>;
                                                        }
                                                        const text = rawMsg.trim();
                                                        if (!text) return <p style={{color:'red', fontSize: '12px'}}>[Empty Message]</p>;

                                                        const isUrl = /^(http|https):\/\/.+/i.test(text);
                                                        const urlWithoutQuery = text.split('?')[0];
                                                        const isImageUrl = isUrl && (/\.(jpeg|jpg|gif|png|webp)$/i.test(urlWithoutQuery) || urlWithoutQuery.endsWith('_blob'));
                                                        
                                                        if (isImageUrl) {
                                                            return (
                                                                <div style={{ position: 'relative' }}>
                                                                    <img 
                                                                        src={text} 
                                                                        alt="attachment" 
                                                                        style={{ maxWidth: "200px", borderRadius: "8px" }} 
                                                                        onError={(e) => {
                                                                            e.currentTarget.style.display = 'none';
                                                                            const errNode = document.createElement('p');
                                                                            errNode.style.color = 'red';
                                                                            errNode.style.fontSize = '10px';
                                                                            errNode.style.wordBreak = 'break-all';
                                                                            errNode.innerText = "Broken image: " + text;
                                                                            e.currentTarget.parentElement?.appendChild(errNode);
                                                                        }}
                                                                    />
                                                                </div>
                                                            );
                                                        } else if (isUrl) {
                                                            let fileName = urlWithoutQuery.split('/').pop() || "Document";
                                                            try { fileName = decodeURIComponent(fileName); } catch (e) {}
                                                            return (
                                                                <a className="chat_doc_link" href={text} target="_blank" rel="noopener noreferrer">
                                                                    <AttachFileIcon style={{ fontSize: "16px" }} />
                                                                    {fileName}
                                                                </a>
                                                            );
                                                        }
                                                        return <p>{text}</p>;
                                                    })()}
                                                </div>
                                                <span
                                                    className="time_sm"
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: isMe ? "flex-end" : "flex-start",
                                                        alignItems: "center",
                                                        fontSize: "10px",
                                                        marginTop: "4px",
                                                        opacity: 0.7,
                                                        gap: "4px"
                                                    }}
                                                >
                                                    {dayjs(msg?.createdAt).format("hh:mm A")}
                                                    {isMe && (
                                                        msg.isRead ? (
                                                            <DoneAllIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                                                        ) : (
                                                            <CheckIcon sx={{ fontSize: 14, color: '#ccc' }} />
                                                        )
                                                    )}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="chat_foot" style={{ flexDirection: "column", alignItems: "flex-start", gap: "10px", width: "100%", padding: "10px 15px", boxSizing: "border-box" }}>
                                    {selectedFile && filePreview && (
                                        <div style={{ padding: "10px", background: "#f5f5f5", borderRadius: "8px", position: "relative", display: "inline-block", border: "1px solid #ddd" }}>
                                            <IconButton 
                                                size="small" 
                                                style={{ position: "absolute", top: -8, right: -8, background: "#ff4d4f", color: "white", zIndex: 10, width: "20px", height: "20px" }}
                                                onClick={removeSelectedFile}
                                            >
                                                <CloseIcon style={{ fontSize: "14px" }} />
                                            </IconButton>
                                            {selectedFile.type.startsWith("image/") ? (
                                                <img src={filePreview} alt="preview" style={{ height: "80px", borderRadius: "4px", objectFit: "contain" }} />
                                            ) : (
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 15px", background: "white", borderRadius: "4px", border: "1px solid #ddd" }}>
                                                    <AttachFileIcon style={{ color: "#666" }} />
                                                    <span style={{ fontSize: "13px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#333", fontWeight: 500 }}>{selectedFile.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="form_input" style={{ width: "100%" }}>
                                        <div className="form">
                                            <TextField
                                                hiddenLabel
                                                placeholder="Type a message..."
                                                fullWidth
                                                value={messageInput}
                                                onChange={(e) => setMessageInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleSendMessage();
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <input
                                                                type="file"
                                                                accept="image/*,.doc,.docx,.pdf"
                                                                style={{ display: "none" }}
                                                                id="file-upload"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) {
                                                                        handleFileSelection(file);
                                                                    }
                                                                    e.target.value = "";
                                                                }}
                                                                disabled={isUploading}
                                                            />
                                                            <label htmlFor="file-upload" style={{ margin: 0, padding: 0 }}>
                                                                <Tooltip title="Attach Media" placement="top">
                                                                    <IconButton component="span" disabled={isUploading}>
                                                                        {isUploading ? <CircularProgress size={24} /> : <AttachFileIcon />}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </label>
                                                            <Tooltip title="Send Message" placement="top">
                                                                <IconButton onClick={handleSendMessage}>
                                                                    <SendIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        ) : (
                            <div className="chat_box1" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: "20px" }}>
                                <div style={{ width: 120, height: 120, borderRadius: '50%', border: '3px dashed #888', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3, marginBottom: '20px' }}>
                                    <SendIcon style={{ fontSize: 50, color: '#888', transform: 'rotate(-45deg)', marginLeft: '8px' }} />
                                </div>
                                <p style={{ color: "#888", fontSize: "18px", fontWeight: 'bold', opacity: 0.5 }}>Select a conversation to start chatting</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
export default Chat;